import { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Pencil, Save, Trash2 } from 'lucide-react';

import PageHeader from '../components/PageHeader.jsx';
import SaveBar, { SaveButton } from '../components/SaveBar.jsx';
import FormField, { TextInput } from '../components/FormField.jsx';
import SearchableSelect from '../components/SearchableSelect.jsx';
import { listProducts } from '../api/products';
import { getQuote, updateQuote } from '../api/quotes';
import { VAT_RATE } from '../constants/vat';

/**
 * Edit Quote — line items only (mirrors EditSale's mobile-scope decision:
 * customer / dates / notes stay locked, complex changes go to desktop).
 *
 * Reachable from QuoteDetail's "Edit line items" action when quote is
 * draft. Backend gates PATCH /quotes/{id}/simple to draft-only — if the
 * quote has moved on between page-load and submit we surface the 400
 * inline rather than silently failing.
 *
 * Simpler than EditSale because quotes don't reserve stock until
 * conversion-to-sale time — no batch picker, no per-line batch fetch.
 * Just product + quantity + unit_price.
 *
 * On save we send only `{ line_items }` — a partial PATCH leaves the
 * rest of the quote (customer, notes, vat_rate) untouched.
 */
export default function EditQuote() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [quoteNumber, setQuoteNumber] = useState(null);

  const [lines, setLines] = useState([]); // [{tempId, product, quantity, unit_price}]
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Synchronous double-tap guard — closes the gap between tap and
  // the `submitting` state update where a second tap could fire.
  const submitInFlight = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    getQuote(id)
      .then((quote) => {
        if (cancelled) return;
        if (quote.status !== 'draft') {
          // Backend rejects non-draft updates with a 400; bouncing back
          // here is a friendlier UX than letting the form load and
          // surface the error after submit.
          navigate(`/quotes/${quote.id}`, {
            replace: true,
            state: { flash: 'Only draft quotes can be edited.' },
          });
          return;
        }
        setQuoteNumber(quote.quote_number ?? `Quote #${quote.id}`);

        // Build initial picker-shape lines from the quote's line items.
        // QuoteOut exposes `unit_price_usd` (the calculator's final
        // per-unit price); we map that into `unit_price` for the form
        // since that's the field name the simple-update endpoint takes.
        const initial = (quote.line_items || []).map((li, idx) => ({
          tempId:     `${li.id}-${idx}`,
          product: {
            id:   li.product_id,
            name: li.product_name ?? `Product #${li.product_id}`,
          },
          quantity:   String(li.quantity ?? ''),
          unit_price: String(li.unit_price_usd ?? ''),
        }));
        setLines(initial);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err.response?.data?.detail || err.message || 'Failed to load quote');
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [id, navigate]);

  const loadProductOptions = useCallback(async (q) => {
    const data = await listProducts({ pageSize: 30, search: q || undefined });
    return data.items.map((p) => ({
      id:   p.id,
      name: p.name,
      sublabel: [p.sku, p.total_stock != null ? `${p.total_stock} in stock` : null]
        .filter(Boolean)
        .join(' · '),
    }));
  }, []);

  function addLine(product) {
    setLines((curr) => [
      ...curr,
      {
        tempId:     Date.now() + Math.random(),
        product,
        quantity:   '1',
        unit_price: '0',
      },
    ]);
  }

  function updateLine(tempId, patch) {
    setLines((curr) => curr.map((l) => (l.tempId === tempId ? { ...l, ...patch } : l)));
  }

  function removeLine(tempId) {
    setLines((curr) => curr.filter((l) => l.tempId !== tempId));
  }

  // Live totals preview — VAT-exclusive subtotal, VAT, grand total.
  // Same convention as NewQuote: unit_price is the final per-unit USD
  // selling price, VAT applied on top.
  const subtotal = lines.reduce(
    (acc, l) => acc + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0),
    0,
  );
  const vat = subtotal * VAT_RATE;
  const total = subtotal + vat;

  async function onSubmit(e) {
    e.preventDefault();
    if (submitInFlight.current) return;
    if (lines.length === 0) {
      setError('Add at least one line item.');
      return;
    }
    // Quantity > 0 — backend rejects zero/negative qtys but with a
    // vague message; catching it here gives a clearer in-context error.
    const linesBadQty = lines.filter((l) => !(Number(l.quantity) > 0));
    if (linesBadQty.length > 0) {
      setError('Every line needs a quantity greater than zero.');
      return;
    }

    submitInFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        line_items: lines.map((l) => ({
          product_id: l.product.id,
          quantity:   Number(l.quantity),
          unit_price: Number(l.unit_price),
        })),
      };
      const updated = await updateQuote(id, payload);
      navigate(`/quotes/${updated.id}`, {
        replace: true,
        state: {
          flash: `Line items updated · ${lines.length} item${lines.length === 1 ? '' : 's'}`,
        },
      });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save changes');
    } finally {
      setSubmitting(false);
      submitInFlight.current = false;
    }
  }

  if (loading) {
    return (
      <main className="min-h-full px-4 pb-6">
        <PageHeader title="Edit Quote" icon={Pencil} />
        <p className="text-small text-gray-500">Loading…</p>
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="min-h-full px-4 pb-6">
        <PageHeader title="Edit Quote" icon={Pencil} />
        <p role="alert" className="text-small text-danger">{loadError}</p>
      </main>
    );
  }

  return (
    <main className="min-h-full px-4 pb-28">
      <PageHeader
        title={quoteNumber ?? 'Edit Quote'}
        subtitle="Edit line items"
        icon={Pencil}
      />

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <p className="text-small font-medium text-gray-700 mb-1.5">Line items</p>
          <div className="space-y-2">
            {lines.map((l) => (
              <div key={l.tempId} className="mb-card space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-body font-medium text-gray-900 truncate">
                      {l.product.name}
                    </p>
                    {l.product.sublabel && (
                      <p className="text-caption text-gray-500 truncate">
                        {l.product.sublabel}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLine(l.tempId)}
                    aria-label="Remove line"
                    className="min-h-touch min-w-touch flex items-center justify-center text-danger"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <FormField label="Qty">
                    <TextInput
                      type="number"
                      inputMode="decimal"
                      min="1"
                      step="1"
                      value={l.quantity}
                      onChange={(e) => updateLine(l.tempId, { quantity: e.target.value })}
                    />
                  </FormField>
                  <FormField label="Unit price (ex VAT)">
                    <TextInput
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={l.unit_price}
                      onChange={(e) => updateLine(l.tempId, { unit_price: e.target.value })}
                    />
                  </FormField>
                </div>
                <p className="text-right text-small text-gray-500">
                  Line total: {((Number(l.quantity) || 0) * (Number(l.unit_price) || 0)).toFixed(2)}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-2">
            <SearchableSelect
              value={null}
              onChange={addLine}
              placeholder="+ Add product"
              loadOptions={loadProductOptions}
              labelOf={(p) => p.name}
            />
          </div>
        </div>

        {/* Live totals preview. Quotes don't have a shipping field on
            the simple flow, so this is a complete picture (unlike the
            EditSale preview which had to flag that shipping was
            excluded). */}
        <section className="mb-card space-y-1">
          <div className="flex justify-between text-small text-gray-600">
            <span>Subtotal (ex VAT)</span>
            <span>{subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-small text-gray-600">
            <span>VAT (15.5%)</span>
            <span>{vat.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-title font-semibold text-gray-900 pt-1 border-t border-gray-200">
            <span>Total</span>
            <span>{total.toFixed(2)}</span>
          </div>
        </section>

        {error && <p role="alert" className="text-small text-danger">{error}</p>}

        <SaveBar>
          <SaveButton type="submit" icon={Save} loading={submitting} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save changes'}
          </SaveButton>
        </SaveBar>
      </form>
    </main>
  );
}
